import { boot } from '@dazn/bootstrap-lib';

import { Hooks } from './hooks';

export interface LoadEventContext {
    chapter: boot.config.ChapterName;
    country: boot.config.AnyCountry;
}

export type LifecycleTriggers = ReturnType<
    typeof createLifecycleHooks
>['triggers'];

export type LifecycleEventHandlers = ReturnType<
    typeof createLifecycleHooks
>['events'];

/**
 * These are similar to a pub/sub architecture. Callbacks are registered to be invoked on specific events. The lifecycle events are used for different tasks such as: Cleaning up listeners on DOM events prior to chapter change, to prevent memory leaks or regressions between chapters.
 */
export interface LifecycleEvents {
    willLoad: LoadEventContext;
    willUnload: LoadEventContext;
    didLoad: LoadEventContext;
    didUnload: LoadEventContext;
    willStart: void;
}

export const createLifecycleHooks = ({
    captureException,
}: {
    captureException: Hooks.ErrorHandler;
}) => {
    const hooks = new Hooks<LifecycleEvents>({
        errorHandler: captureException,
    });

    const createAddListenerMethod =
        <N extends keyof LifecycleEvents>(eventName: N) =>
        (
            callback: Hooks.Callback,
            options?: Partial<Hooks.Options<LifecycleEvents[N]>>,
        ) =>
            hooks.addHook(eventName, callback, options);

    const onChapterWillLoad = createAddListenerMethod('willLoad');
    const onChapterWillUnload = createAddListenerMethod('willUnload');
    const onChapterDidLoad = createAddListenerMethod('didLoad');
    const onChapterDidUnload = createAddListenerMethod('didUnload');
    const onWillStart = createAddListenerMethod('willStart');

    const onChapterUnload = onChapterWillLoad;
    const onChapterLoad = onChapterDidLoad;

    const createFireHookMethod =
        <N extends keyof LifecycleEvents>(eventName: N) =>
        (context: LifecycleEvents[N]) =>
            hooks.fireHooks<N>(eventName, context);

    const willLoadEvents = createFireHookMethod('willLoad');
    const willUnloadEvents = createFireHookMethod('willUnload');
    const didLoadEvents = createFireHookMethod('didLoad');
    const didUnloadEvents = createFireHookMethod('didUnload');
    const willStartEvents = () => hooks.fireHooks('willStart', undefined);

    return {
        triggers: {
            willLoadEvents,
            willUnloadEvents,
            didLoadEvents,
            didUnloadEvents,
            willStartEvents,
        },
        events: {
            onChapterLoad,
            onChapterUnload,
            onChapterWillLoad,
            onChapterWillUnload,
            onChapterDidLoad,
            onChapterDidUnload,
            onWillStart,
        },
    };
};
